import { storeToRefs } from 'pinia';
import { nextTick } from 'vue';
import { Route } from 'vue-router';
import { getFromSessionStorage } from '@/apps/order/helper';
import { useOrdersCreateProgressStore } from '@/apps/order/stores/ordersCreate';
import ordersCreateRouter from '@/apps/order/routes/ordersCreate';

let alreadyFetched = false;

const canAccessStep = async (to: Route, from: Route | undefined): Promise<void> => new Promise<void>((resolve, reject) => {
  const ctx = to.meta?.ctx;
  const defaultError = new Error('access check failed');
  nextTick(async () => {
    try {
      const progressStore = useOrdersCreateProgressStore();
      const { accessibleSteps, fromRoute } = storeToRefs(progressStore);

      if (from) {
        fromRoute.value = from;
      }

      let storedAccessibleSteps;

      if (!alreadyFetched) {
        storedAccessibleSteps = getFromSessionStorage<Record<string, boolean>>(`${ctx}.accessibleSteps`);
        alreadyFetched = true;
      }

      if (storedAccessibleSteps !== undefined) {
        accessibleSteps.value = storedAccessibleSteps;
      }

      if (to.meta?.mode === 'edit') {
        progressStore.setStepAccessible('type');
        progressStore.setStepAccessible('category');
        progressStore.setStepAccessible('calculator');
        progressStore.setStepAccessible('address');
      }

      if (to.meta?.mode === 'tariff-change') {
        progressStore.setStepAccessible('type');
        progressStore.setStepAccessible('category');
        progressStore.setStepAccessible('calculator');
        progressStore.setStepAccessible('address');
        progressStore.setStepAccessible('consumptionPoint');
        progressStore.setStepAccessible('payment');
        progressStore.setStepAccessible('customFields');
        progressStore.setStepAccessible('overview');
      }

      // For relocation mode, all steps should be accessible since data is pre-filled
      if (to.meta?.mode === 'relocation') {
        progressStore.setStepAccessible('type');
        progressStore.setStepAccessible('category');
        progressStore.setStepAccessible('calculator');
        progressStore.setStepAccessible('address');
        progressStore.setStepAccessible('consumptionPoint');
        progressStore.setStepAccessible('payment');
        progressStore.setStepAccessible('customFields');
        progressStore.setStepAccessible('overview');
      }

      const toStepKey = to.meta?.step;

      if (typeof toStepKey === 'string') {
        const stepIsAccessible = accessibleSteps.value[toStepKey] === true;

        if (!stepIsAccessible && from?.meta?.step) {
          const stepOrder = ['type', 'customer', 'category', 'calculator', 'address', 'consumptionPoint', 'payment', 'customFields', 'overview'];
          const fromStepIndex = stepOrder.indexOf(from.meta.step as string);
          const toStepIndex = stepOrder.indexOf(toStepKey);

          // Allow backward navigation if we're going to a previous step
          const isBackwardNavigation = fromStepIndex > toStepIndex && toStepIndex >= 0;
          const fromStepWasAccessible = accessibleSteps.value[from.meta.step as string] === true;

          if (isBackwardNavigation && fromStepWasAccessible) {
            // Make the target step accessible for backward navigation
            progressStore.setStepAccessible(toStepKey, true);
            resolve();
            return;
          }
        }

        if (!stepIsAccessible) {
          const lastAccessibleStep = Object.keys(accessibleSteps.value).filter((stepKey) => accessibleSteps.value[stepKey] === true).pop();

          const nextRoute = ordersCreateRouter.getRoutes().find((route) => route.meta.step === lastAccessibleStep);

          if (nextRoute === undefined) {
            reject(defaultError);
          } else {
            defaultError.message = `to:${nextRoute.name}`;
            reject(defaultError);
          }

          return;
        }
      }

      resolve();
    } catch (e) {
      reject(defaultError);
    }
  });
});

export default canAccessStep;
